import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AttestationPresence } from '@app/models/attestation-presence/attestation-presence';
import { AttestationPresenceService } from '@app/models/attestation-presence/attestation-presence.service';
import { AttestationPresenceSearchParams } from '@app/models/attestation-presence/attestation-presence-search-params';
import { User } from '@app/models/user/user';
import { DocumentAdministrative } from '@app/models/document-administrative';
import { FormationSession } from '@app/models/formation-session/formation-session';
import {
	FormationSessionService,
	PresenceSummaryResponse,
} from '@app/models/formation-session/formation-session.service';

// Simplified interface for attestation presence
interface SimpleAttestationPresence {
	_id?: string;
	owner: User | string; // Only required field
	document?: any;
	status?: string;
	created_at?: Date;
	isPartial?: boolean;
	total_time?: number;
	total_time_override?: number;
	total_time_percent?: number;
	total_presence?: number; // From new presence endpoint
	total_hours?: number; // From new presence endpoint
}

@Component({
	selector: 'app-atom-formation-table-attestation-presence',
	templateUrl: './atom-formation-table-attestation-presence.component.html',
	styleUrls: ['./atom-formation-table-attestation-presence.component.less'],
})
export class AtomFormationTableAttestationPresenceComponent
	implements OnInit, OnChanges
{
	@Input() title: string;
	@Input() sessionId: string;
	@Input() respondants: User[] = [];
	@Input() disableUpload = true; // Disabled by default as requested
	@Input() disableGenerate: boolean;
	@Input() disableSend: boolean;
	@Input() disableDelete = true;
	@Input() generateLoading = false;
	@Input() templateOptions: string[] = []; // Available template options
	@Input() defaultTemplate: string | null = null;
	@Input() showButtonSendGrouped = false;

	@Output() emitTemplateChange = new EventEmitter<string>(); // Emit when template changes
	@Output() emitGenerate = new EventEmitter<{
		docType: string;
		template: string;
		totalTimeOverride?: number;
		respondantTimeOverrides?: { [respondantId: string]: number };
	}>(); // Updated to include template, totalTimeOverride, and respondantTimeOverrides
	@Output() emitUpload = new EventEmitter<File>(); // upload for a doc type
	@Output() emitSendGrouped = new EventEmitter<void>(); // send grouped for a doc type
	@Output() emitGenerateForRespondant = new EventEmitter<{
		docType: string;
		respondantId: string;
		template: string;
		totalTimeOverride?: number;
	}>(); // Updated to include template and totalTimeOverride

	@Output() emitReadDocument = new EventEmitter<string>();
	@Output() emitSendDocument = new EventEmitter<string>();
	@Output() emitDeleteDocument = new EventEmitter<string>();
	@Output() emitDownloadDocument = new EventEmitter<{
		attestationId: string;
		filename: string;
	}>();

	// New outputs for bulk actions
	@Output() emitBulkGenerate = new EventEmitter<{
		selectedIds: string[];
		partialRespondantIds?: string[];
		template: string;
		totalTimeOverride?: number;
		respondantTimeOverrides?: { [respondantId: string]: number };
	}>();
	@Output() emitBulkSend = new EventEmitter<string[]>();
	@Output() emitBulkDelete = new EventEmitter<string[]>();
	@Output() emitBulkDownload = new EventEmitter<
		{ attestationId: string; filename: string }[]
	>();

	@ViewChild('btnsContainer') btnsContainer: ElementRef;

	// Modal properties
	showGenerateModal = false;
	showRespondantModal = false;
	selectedTemplate: string = '';
	globalTimeOverride: number = null;
	respondantTimeOverride: number = null;
	respondantModalTitle: string = '';
	respondantDefaultTimeOverride: number = null;
	currentRespondantId: string = '';

	defaultTimeOverride: number = null;

	// Selection properties
	selectedItems: Record<string, boolean> = {};

	// Cached computed values
	selectedCount: number = 0;
	hasSelected: boolean = false;
	canSend: boolean = false;
	canDelete: boolean = false;
	canBulkDownload: boolean = false;
	hasPartial: boolean = false;

	private selectedFile: File;
	attestations: AttestationPresence[] = [];
	displayItems: SimpleAttestationPresence[] = [];
	loading = false;

	// Store data for time calculations
	sessionData: FormationSession = null;

	// Map to store previous overrides for respondants
	previousOverrides: { [respondantId: string]: number } = {};

	// Store presence summary from new endpoint
	presenceSummaryData: PresenceSummaryResponse = null;

	// Checkbox state for override toggle in modal
	useOverride: boolean = false;

	constructor(
		public translateService: TranslateService,
		private attestationPresenceService: AttestationPresenceService,
		private sessionService: FormationSessionService
	) {}

	async ngOnInit(): Promise<void> {
		// Initialize selectedTemplate if templateOptions are available
		if (this.templateOptions && this.templateOptions.length > 0) {
			this.selectedTemplate =
				this.defaultTemplate || this.templateOptions[0];
		}

		// Get default time override
		if (this.sessionId) {
			this.defaultTimeOverride =
				await this.calculateDefaultTimeOverride();

			this.globalTimeOverride = this.defaultTimeOverride;
		}

		await this.refresh();
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (
			(changes.sessionId && !changes.sessionId.firstChange) ||
			(changes.respondants && !changes.respondants.firstChange)
		) {
			if (changes.sessionId) {
				// Update default time override if session ID changes
				this.defaultTimeOverride =
					await this.calculateDefaultTimeOverride();
				this.globalTimeOverride = this.defaultTimeOverride;
			}
			await this.refresh();
		}

		// Initialize selectedTemplate when templateOptions changes
		if (
			changes.templateOptions &&
			this.templateOptions &&
			this.templateOptions.length > 0
		) {
			this.selectedTemplate = this.templateOptions[0];
		}
	}

	async refresh(): Promise<void> {
		if (!this.sessionId) {
			this.updateDisplayItems();
			return;
		}

		this.loading = true;

		// Clear selections on refresh
		this.selectedItems = {};

		// Load data for time calculations
		try {
			if (this.sessionId) {
				this.sessionData = await this.sessionService.getAsAdmin(
					this.sessionId
				);
				this.defaultTimeOverride =
					await this.calculateDefaultTimeOverride();

				// Initialize globalTimeOverride if not already set
				if (this.globalTimeOverride === null) {
					this.globalTimeOverride = this.defaultTimeOverride;
				}
			}
		} catch (error) {
			console.error('Error loading data:', error);
		}

		const searchParams = new AttestationPresenceSearchParams();
		const searchParamsObj: any = {
			_page: 0,
			_limit: 100,
			_populate: ['stagiaire', 'document'],
		};

		// Set the appropriate ID for the search
		if (this.sessionId) {
			searchParamsObj.session = this.sessionId;
		}

		searchParams.fromObject(searchParamsObj);

		const resultItems = await this.attestationPresenceService
			.listAsAdmin(searchParams.toObject())
			.then((result) => result.items)
			.catch((error) => {
				console.error('Error loading attestations:', error);
				this.updateDisplayItems();
				this.loading = false;

				return [];
			});

		this.attestations = resultItems;

		// Store previous overrides for respondants
		this.updatePreviousOverrides();

		this.updateDisplayItems();
		this.loading = false;

		// Initialize cached selection state
		this.refreshSelectionState();
	}

	updatePreviousOverrides(): void {
		// Reset previous overrides
		this.previousOverrides = {};

		// Store overrides from existing attestations
		if (this.attestations && this.attestations.length > 0) {
			this.attestations.forEach((attestation) => {
				if (
					attestation.stagiaireExists() &&
					attestation.documentExists()
				) {
					const doc = attestation.props.document;
					if (doc instanceof DocumentAdministrative) {
						if (
							doc.props.data?.formation?.session
								?.total_time_override
						) {
							const respondantId =
								attestation.props.stagiaire instanceof User
									? attestation.props.stagiaire.props._id
									: (attestation.props.stagiaire as string);

							this.previousOverrides[respondantId] =
								doc.props.data.formation.session.total_time_override;
						}
					}
				}
			});
		}
	}

	updateDisplayItems(): void {
		this.displayItems = [];

		// Add all attestations
		if (this.attestations && this.attestations.length > 0) {
			this.attestations.forEach((attestation) => {
				let document = null;
				let status = null;
				let created_at = null;
				let total_time = null;
				let total_time_override = null;
				let total_time_percent = null;

				if (attestation.documentExists()) {
					const doc = attestation.props.document;
					if (doc instanceof DocumentAdministrative) {
						document = doc;
						status = doc.props.status;
						created_at = doc.props.created_at;

						if (doc.props.data?.formation?.session) {
							const session = doc.props.data.formation.session;
							total_time = session.total_time || null;
							total_time_override =
								session.total_time_override || null;
							total_time_percent =
								session.total_time_percent || null;
						}
					}
				}

				// Get respondant ID and presence data from new endpoint
				const respondantId =
					attestation.props.stagiaire instanceof User
						? attestation.props.stagiaire.props._id
						: (attestation.props.stagiaire as string);

				const total_presence = this.getRespondantPresence(respondantId);
				const total_hours = this.presenceSummaryData
					? this.presenceSummaryData.total_hours
					: null;

				this.displayItems.push({
					_id: attestation.props._id,
					owner: attestation.props.stagiaire as User,
					document,
					status,
					created_at,
					total_time,
					total_time_override,
					total_time_percent,
					total_presence,
					total_hours,
					isPartial: false,
				});
			});
		}

		// Add partial items for missing respondants
		if (this.respondants && this.respondants.length > 0) {
			this.respondants.forEach((respondant) => {
				const existingAttestation = this.displayItems.find((item) => {
					if (item.owner instanceof User) {
						return item.owner.props._id === respondant.props._id;
					} else {
						return item.owner === respondant.props._id;
					}
				});

				if (!existingAttestation) {
					// Get presence data for partial items
					const total_presence = this.getRespondantPresence(
						respondant.props._id
					);
					const total_hours = this.presenceSummaryData
						? this.presenceSummaryData.total_hours
						: null;

					this.displayItems.push({
						owner: respondant,
						total_presence,
						total_hours,
						isPartial: true,
					});
				}
			});
		}

		// Clean up selected items that no longer exist
		this.cleanupSelectedItems();

		// Refresh cached computed values
		this.refreshSelectionState();
	}

	// Clean up selected items that no longer exist in displayItems
	private cleanupSelectedItems(): void {
		const validItemIds = new Set<string>();

		// Collect all valid item IDs
		this.displayItems.forEach((item) => {
			const itemId = this.getItemId(item);
			if (itemId) {
				validItemIds.add(itemId);
			}
		});

		// Remove selected items that are no longer valid
		Object.keys(this.selectedItems).forEach((selectedId) => {
			if (!validItemIds.has(selectedId)) {
				delete this.selectedItems[selectedId];
			}
		});
	}

	// Selection methods
	getItemId(item: SimpleAttestationPresence): string | null {
		if (item._id) {
			return item._id;
		} else if (item.isPartial && item.owner) {
			const ownerId =
				typeof item.owner === 'string'
					? item.owner
					: item.owner.props._id;
			return 'partial_' + ownerId;
		}
		return null;
	}

	isItemSelected(item: SimpleAttestationPresence): boolean {
		const itemId = this.getItemId(item);
		return itemId ? !!this.selectedItems[itemId] : false;
	}

	refreshSelectionState(): void {
		// Update selectedCount
		this.selectedCount = Object.values(this.selectedItems).filter(
			(selected) => selected
		).length;

		// Update hasSelected
		this.hasSelected = this.selectedCount > 0;

		// Update hasPartial - check if any partial items are selected
		this.hasPartial = Object.entries(this.selectedItems).some(
			([id, isSelected]) => isSelected && id.startsWith('partial_')
		);

		// Update canSend - based on new requirements
		this.canSend = this.areAllSelectedItemsSendable();

		// Update canBulkDownload - similar to canSend logic
		this.canBulkDownload = this.areAllSelectedItemsDownloadable();

		// Update canDelete - can delete if has selected items AND no partial items
		this.canDelete =
			this.hasSelected &&
			!this.hasPartial &&
			Object.entries(this.selectedItems).some(([id, isSelected]) => {
				if (!isSelected || id.startsWith('partial_')) return false;
				const item = this.displayItems.find((item) => item._id === id);
				return item && this.canDownload(item);
			});
	}

	toggleSelectAll(): void {
		const allSelected = this.isSelectAllChecked();

		if (allSelected) {
			// If all are selected, unselect all
			this.selectedItems = {};
		} else {
			// If none or some are selected, select all
			this.displayItems.forEach((item) => {
				const itemId = this.getItemId(item);
				if (itemId) {
					this.selectedItems[itemId] = true;
				}
			});
		}

		// Refresh cached computed values
		this.refreshSelectionState();
	}

	toggleItemSelection(item: SimpleAttestationPresence): void {
		const itemId = this.getItemId(item);
		if (!itemId) return;

		if (this.isItemSelected(item)) {
			delete this.selectedItems[itemId];
		} else {
			this.selectedItems[itemId] = true;
		}

		// Refresh cached computed values
		this.refreshSelectionState();
	}

	getSelectedCount(): number {
		return this.selectedCount;
	}

	hasSelectedItems(): boolean {
		return this.hasSelected;
	}

	isSelectAllChecked(): boolean {
		if (this.displayItems.length === 0) return false;

		// Count how many items are actually selected in selectedItems
		const selectedCount = this.displayItems.filter((item) =>
			this.isItemSelected(item)
		).length;

		// Return true only if ALL items are selected
		return selectedCount > 0 && selectedCount === this.displayItems.length;
	}

	canGenerateSelected(): boolean {
		// All selected items can be generated (both partial and real attestations)
		return this.hasSelected;
	}

	canSendSelected(): boolean {
		return this.canSend;
	}

	canDeleteSelected(): boolean {
		return this.canDelete;
	}

	// Button disable logic based on user requirements
	shouldDisableSend(): boolean {
		// Use the new canSend logic which checks generation status
		return !this.canSend;
	}

	shouldDisableDelete(): boolean {
		// Disable delete if any partial row is selected OR if no row selected
		return this.hasPartial || !this.hasSelected;
	}

	// Helper method to check if any partial items are selected
	hasPartialSelected(): boolean {
		return this.hasPartial;
	}

	// Bulk action methods
	handleBulkGenerate(): void {
		// Always show the modal dialog to select template and time override
		this.showGenerateModal = true;
	}

	handleBulkSend(): void {
		let idsToSend: string[] = [];

		if (!this.hasSelectedItems()) {
			// If no rows selected, send all sendable items
			idsToSend = this.displayItems
				.filter((item) => this.canSendItem(item))
				.map((item) => item._id!);
		} else {
			// Send only selected items that are sendable
			idsToSend = Object.entries(this.selectedItems)
				.filter(([id, isSelected]) => {
					if (!isSelected) return false; // Skip unselected items
					if (id.startsWith('partial_')) return false; // Skip partial items
					const item = this.displayItems.find(
						(item) => item._id === id
					);
					return item && this.canSendItem(item);
				})
				.map(([id]) => id);
		}

		if (idsToSend.length > 0) {
			this.emitBulkSend.emit(idsToSend);
		}
	}

	handleBulkDelete(): void {
		if (!this.hasSelectedItems()) return;

		const selectedIds = Object.entries(this.selectedItems)
			.filter(([id, isSelected]) => {
				if (!isSelected) return false; // Skip unselected items
				const item = this.displayItems.find((item) => item._id === id);
				return item && this.canDownload(item);
			})
			.map(([id]) => id);

		if (selectedIds.length > 0) {
			this.emitBulkDelete.emit(selectedIds);
		}
	}

	// Helper methods for the template
	getOwnerName(item: SimpleAttestationPresence): string {
		if (!item.owner) return '-';

		if (typeof item.owner === 'string') {
			return item.owner; // Just return the ID if it's a string
		} else if (item.owner instanceof User) {
			// Access the properties safely using any to bypass type checking
			const user = item.owner as any;
			const firstName =
				user.props.firstname || user.props.first_name || '';
			const lastName = user.props.lastname || user.props.last_name || '';
			return `${firstName} ${lastName}`;
		}

		return '-';
	}

	getDocumentLabel(item: SimpleAttestationPresence): string {
		if (!item.document || item.isPartial) return '-';

		if (item.document instanceof DocumentAdministrative) {
			return item.document.props.label || '-';
		}

		return '-';
	}

	getDocumentId(item: SimpleAttestationPresence): string {
		if (!item.document || item.isPartial) return '';

		if (item.document instanceof DocumentAdministrative) {
			return item.document.props._id || '';
		}

		return '';
	}

	getDocumentStatus(item: SimpleAttestationPresence): string {
		return item.status || '';
	}

	getCreatedDate(item: SimpleAttestationPresence): Date {
		if (!item.document || !item.created_at || item.isPartial) return null;
		return item.created_at;
	}

	canDownload(item: SimpleAttestationPresence): boolean {
		return !item.isPartial && !!item.document;
	}

	// Check if an item is sendable (has generated document)
	canSendItem(item: SimpleAttestationPresence): boolean {
		if (!item.document || item.isPartial) return false;
		const status = this.getDocumentStatus(item);
		return status === 'generated' || status === 'created';
	}

	// Check if any items are sendable when no selection
	hasAnySendableItems(): boolean {
		return this.displayItems.some((item) => this.canSendItem(item));
	}

	// Check if all selected items are sendable
	areAllSelectedItemsSendable(): boolean {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		// If no items selected, check if there are any sendable items
		if (selectedKeys.length === 0) {
			return this.hasAnySendableItems();
		}

		// Check ALL selected items (including partial items)
		// If ANY selected item is not sendable, return false
		return selectedKeys.every((key) => {
			if (key.startsWith('partial_')) {
				// Partial items are never sendable
				return false;
			}

			const item = this.displayItems.find((item) => item._id === key);
			return item && this.canSendItem(item);
		});
	}

	// Calculate default time override based on session start and end dates
	// This method now uses the new presence endpoint
	calculateDefaultTimeOverride(): Promise<number> {
		return this.sessionService
			.getPresenceSummaryAsAdmin(this.sessionId)
			.then((presenceSummary) => {
				this.presenceSummaryData = presenceSummary;
				return presenceSummary.total_hours;
			})
			.catch((error) => {
				console.error(
					'Error calculating default time override:',
					error
				);
				return 0;
			});
	}

	// Get previous override for a respondant or default if none exists
	getTimeOverrideForRespondant(respondantId: string): number {
		// If we have a previous override for this respondant, use it
		if (this.previousOverrides[respondantId]) {
			return this.previousOverrides[respondantId];
		}

		// Otherwise use the default calculation
		return this.defaultTimeOverride;
	}

	// Get total_presence for a specific respondant from presence summary
	getRespondantPresence(respondantId: string): number | null {
		if (
			!this.presenceSummaryData ||
			!this.presenceSummaryData.respondants
		) {
			return null;
		}

		const respondantData = this.presenceSummaryData.respondants.find(
			(r) => r.respondant._id === respondantId
		);

		return respondantData ? respondantData.total_presence : null;
	}

	// Method to open respondant modal
	openRespondantModal(item: SimpleAttestationPresence): void {
		if (!item || !item.owner) return;

		let respondantId = '';
		if (typeof item.owner === 'string') {
			respondantId = item.owner;
		} else if (item.owner instanceof User) {
			respondantId = item.owner.props._id;
		}

		if (!respondantId) return;

		this.currentRespondantId = respondantId;
		// Use the respondant's total_presence as the default override value
		this.respondantDefaultTimeOverride =
			this.getRespondantPresence(respondantId);

		if (
			this.respondantDefaultTimeOverride === null ||
			this.respondantDefaultTimeOverride === undefined
		) {
			this.respondantDefaultTimeOverride = this.defaultTimeOverride;
		}
		this.respondantTimeOverride = this.respondantDefaultTimeOverride;

		// Only set selectedTemplate if it's not already set
		if (!this.selectedTemplate && this.templateOptions.length > 0) {
			this.selectedTemplate = this.templateOptions[0];
		}

		this.respondantModalTitle = this.translateService.instant(
			'generate_attestation_for',
			{
				name: this.getOwnerName(item),
			}
		);
		this.showRespondantModal = true;
	}

	// Handle global generate submit
	handleGenerateSubmit(): void {
		if (!this.selectedTemplate) {
			console.error('No template selected');
			return;
		}

		this.showGenerateModal = false;

		// Build respondant time overrides map
		const respondantTimeOverrides: { [respondantId: string]: number } = {};

		if (this.hasSelected) {
			// Generate for selected items
			const selectedIds = Object.entries(this.selectedItems)
				.filter(([id, isSelected]) => isSelected)
				.map(([id]) => id);
			const realAttestationIds: string[] = [];
			const partialRespondantIds: string[] = [];

			selectedIds.forEach((id) => {
				if (id.startsWith('partial_')) {
					// Extract the respondant ID from partial items
					const respondantId = id.replace('partial_', '');
					partialRespondantIds.push(respondantId);

					// Set time override for this respondant
					respondantTimeOverrides[respondantId] = this.useOverride
						? this.globalTimeOverride
						: this.getRespondantPresence(respondantId);
				} else {
					// Real attestation ID - get respondant ID from display item
					const item = this.displayItems.find((i) => i._id === id);
					if (item && item.owner) {
						const respondantId =
							item.owner instanceof User
								? item.owner.props._id
								: (item.owner as string);
						realAttestationIds.push(id);

						// Set time override for this respondant
						respondantTimeOverrides[respondantId] = this.useOverride
							? this.globalTimeOverride
							: this.getRespondantPresence(respondantId);
					}
				}
			});

			this.emitBulkGenerate.emit({
				selectedIds: realAttestationIds,
				partialRespondantIds: partialRespondantIds,
				template: this.selectedTemplate,
				totalTimeOverride: this.useOverride
					? this.globalTimeOverride
					: null,
				respondantTimeOverrides: respondantTimeOverrides,
			});
		} else {
			// Generate for all respondants
			this.respondants.forEach((respondant) => {
				respondantTimeOverrides[respondant.props._id] = this.useOverride
					? this.globalTimeOverride
					: this.getRespondantPresence(respondant.props._id);
			});

			this.emitGenerate.emit({
				docType: 'attestation_presence',
				template: this.selectedTemplate,
				totalTimeOverride: this.useOverride
					? this.globalTimeOverride
					: null,
				respondantTimeOverrides: respondantTimeOverrides,
			});
		}
	}

	// Handle respondant generate submit
	handleRespondantGenerateSubmit(): void {
		if (!this.selectedTemplate || !this.currentRespondantId) {
			console.error('Missing required data for generation');
			return;
		}

		this.showRespondantModal = false;

		this.emitGenerateForRespondant.emit({
			docType: 'attestation_presence',
			respondantId: this.currentRespondantId,
			template: this.selectedTemplate,
			totalTimeOverride: this.respondantTimeOverride,
		});
	}

	onFileSelect(event) {
		this.selectedFile = event.target.files[0];
		this.emitUpload.emit(this.selectedFile);
	}

	hasGeneratedDocuments(): boolean {
		// Check if there are any generated documents to send
		return this.displayItems.some((item) => {
			if (!item.document || item.isPartial) return false;

			const status = this.getDocumentStatus(item);
			return status === 'generated' || status === 'created';
		});
	}

	// Check if any items are downloadable when no selection
	hasAnyDownloadableItems(): boolean {
		return this.displayItems.some((item) => this.canDownload(item));
	}

	// Check if all selected items are downloadable
	areAllSelectedItemsDownloadable(): boolean {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		// If no items selected, check if there are any downloadable items
		if (selectedKeys.length === 0) {
			return this.hasAnyDownloadableItems();
		}

		// Check ALL selected items (including partial items)
		// If ANY selected item is not downloadable, return false
		return selectedKeys.every((key) => {
			if (key.startsWith('partial_')) {
				// Partial items are never downloadable
				return false;
			}

			const item = this.displayItems.find((item) => item._id === key);
			return item && this.canDownload(item);
		});
	}

	handleBulkDownload(): void {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		if (selectedKeys.length === 0) {
			// No selection, download all downloadable items
			const allDownloadableItems = this.displayItems
				.filter((item) => item._id && this.canDownload(item))
				.map((item) => ({
					attestationId: item._id,
					filename: this.getDocumentLabel(item),
				}));
			this.emitBulkDownload.emit(allDownloadableItems);
		} else {
			// Download only selected items that are downloadable
			const selectedDownloadableItems = selectedKeys
				.filter((key) => !key.startsWith('partial_'))
				.map((id) => {
					const item = this.displayItems.find(
						(item) => item._id === id
					);
					return item && this.canDownload(item)
						? {
								attestationId: item._id,
								filename: this.getDocumentLabel(item),
						  }
						: null;
				})
				.filter((item) => item !== null);

			if (selectedDownloadableItems.length > 0) {
				this.emitBulkDownload.emit(selectedDownloadableItems);
			}
		}
	}

	shouldDisableDownload(): boolean {
		return !this.canBulkDownload;
	}
}
